import React, { memo, useCallback, useMemo } from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import { Checkbox } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DiaryEntrySyncStatus } from 'graphql/types';
import { useSmartDiaryCheckboxIcons } from '../hooks/useSmartDiaryCheckboxIcons';
import { SmartDiaryEntry } from '../type';
import { useSmartEntryUnselectState } from '../hooks/useSmartEntryUnselectState';

type FormCheckboxProps = {
  smartEntry?: SmartDiaryEntry;
  name: string;
  disabled?: boolean;
};

const useStyles = makeStyles((theme) => ({
  disabled: { opacity: 0.4 },
  root: {
    padding: theme.spacing(0.875, 1.125),
    color: theme.palette.grey[300],
    '&.Mui-checked': {
      color: ({ isSynced }: { isSynced: boolean }) =>
        isSynced ? theme.palette.error.dark : theme.palette.grey[700]
    },
    '&:hover': {
      color: theme.palette.grey[500]
    },
    '&.Mui-checked:hover': {
      color: theme.palette.grey[700]
    }
  }
}));

export const FormCheckbox: React.FC<FormCheckboxProps> = memo(
  ({ name, disabled, smartEntry }: FormCheckboxProps) => {
    const { control, setValue, getValues } = useFormContext();
    const {
      addUnselectedEntries,
      removeUnselectedEntries
    } = useSmartEntryUnselectState();

    const onChange = useCallback(
      (e) => {
        setValue(name, e.target.checked, { shouldDirty: true });
        if (smartEntry?.syncStatus === DiaryEntrySyncStatus.UpdatePending) {
          if (e.target.checked === true) {
            removeUnselectedEntries([name]);
          } else if (e.target.checked === false) {
            addUnselectedEntries([name]);
          }
        }
      },
      [
        addUnselectedEntries,
        name,
        removeUnselectedEntries,
        setValue,
        smartEntry?.syncStatus
      ]
    );
    const inputProps = useMemo(
      () => ({
        inputProps: { 'data-testid': `clickable_checkbox_${name}` } as any
      }),
      [name]
    );
    const iconProps = useSmartDiaryCheckboxIcons();
    const value = useWatch({
      name,
      control,
      defaultValue: getValues()[name]
    });

    const classes = useStyles({
      isSynced: smartEntry?.syncStatus === DiaryEntrySyncStatus.Synced
    });
    return (
      <Checkbox
        color={'default'}
        classes={classes}
        onChange={onChange}
        checked={Boolean(value)}
        data-testid={`checkbox_${name}`}
        {...inputProps}
        {...iconProps}
        disabled={disabled}
      />
    );
  }
);
